import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Send, Moon, Calendar, Crown, Lock } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const AITips = () => {
  const { token } = useAuth();
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [tips, setTips] = useState(null);
  const [context, setContext] = useState({ cycle_phase: "", moon_phase: "" });
  const [symptoms, setSymptoms] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [limitReached, setLimitReached] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const symptomOptions = ["Cramps", "Fatigue", "Mood swings", "Bloating", "Headache", "Anxiety"];

  useEffect(() => {
    fetchContext();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchContext = async () => {
    try {
      const [moonRes, cycleRes] = await Promise.all([
        axios.get(`${API}/moon/current`),
        axios.get(`${API}/cycle/current`, { headers, withCredentials: true })
      ]);
      
      setContext({
        cycle_phase: cycleRes.data.phase !== "unknown" ? cycleRes.data.phase_name : "Unknown",
        moon_phase: moonRes.data.phase_name
      });
    } catch (error) {
      // data loads with fallback state
    } finally {
      setInitialLoading(false);
    }
  };

  const toggleSymptom = (symptom) => {
    setSymptoms(prev =>
      prev.includes(symptom)
        ? prev.filter(s => s !== symptom)
        : [...prev, symptom]
    );
  };

  const handleGetTips = async () => {
    setLoading(true);
    setLimitReached(null);
    try {
      const res = await axios.post(`${API}/ai/tips`, {
        cycle_phase: context.cycle_phase || "Unknown",
        moon_phase: context.moon_phase,
        symptoms,
        question: question || null
      }, { headers, withCredentials: true });
      
      // Check if limit reached
      if (res.data.error === "limit_reached") {
        setLimitReached(res.data);
        toast.info("Monthly limit reached - upgrade for unlimited access");
        return;
      }
      
      setTips(res.data.tips);
      if (res.data.fallback) {
        toast.info("Using curated recommendations");
      }
    } catch (error) {
      toast.error("Failed to get tips");
      // data loads with fallback state
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-tips-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-mystic-300" />
          AI Wellness Guide
        </h1>
        <p className="text-mystic-200/70">Personalized guidance powered by Claude AI</p>
      </motion.div>

      {/* Current Context */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="glass">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4 justify-center">
              <div className="flex items-center gap-2 px-4 py-2 bg-mystic-700/30 rounded-full">
                <Calendar className="w-4 h-4 text-cycle-ovulation" />
                <span className="text-mystic-100">{context.cycle_phase}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-mystic-700/30 rounded-full">
                <Moon className="w-4 h-4 text-moon-gold" />
                <span className="text-mystic-100">{context.moon_phase}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="glass h-full">
            <CardHeader>
              <CardTitle className="text-lg text-white">Ask for Guidance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Symptoms */}
              <div className="space-y-2">
                <label className="text-mystic-100 text-sm">Current symptoms (optional)</label>
                <div className="flex flex-wrap gap-2">
                  {symptomOptions.map((symptom) => (
                    <Button
                      key={symptom}
                      type="button"
                      variant={symptoms.includes(symptom) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleSymptom(symptom)}
                      className={symptoms.includes(symptom) 
                        ? "bg-mystic-300 text-white" 
                        : "border-white/20 text-mystic-200 hover:bg-mystic-700"
                      }
                    >
                      {symptom}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Question */}
              <div className="space-y-2">
                <label className="text-mystic-100 text-sm">Your question (optional)</label>
                <Textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="What would you like guidance on? Leave empty for general tips..."
                  className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30 min-h-[120px]"
                  data-testid="question-textarea"
                />
              </div>

              <Button
                onClick={handleGetTips}
                disabled={loading}
                className="w-full bg-mystic-300 hover:bg-mystic-200 text-white rounded-full"
                data-testid="get-tips-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Get Personalized Tips
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Response Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="glass h-full">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-moon-gold" />
                Your Guidance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {limitReached ? (
                <div className="text-center py-8">
                  <div className="glass-light rounded-xl p-6 border border-moon-gold/30 bg-moon-gold/5">
                    <Lock className="w-12 h-12 text-moon-gold mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">Monthly Limit Reached</h3>
                    <p className="text-mystic-200/70 mb-4">
                      You've used all {limitReached.tips_limit} free AI tips this month.
                    </p>
                    <p className="text-mystic-200/70 mb-6">
                      Upgrade to Premium for <span className="text-moon-gold">unlimited AI guidance</span>, 
                      detailed natal charts, planetary transits, and more!
                    </p>
                    <Link to="/settings">
                      <Button className="bg-moon-gold hover:bg-moon-gold/90 text-mystic-900">
                        <Crown className="w-4 h-4 mr-2" />
                        Upgrade to Premium
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : tips ? (
                <div className="prose prose-invert prose-mystic max-w-none">
                  <div className="glass-light rounded-xl p-6">
                    <p className="text-mystic-100 whitespace-pre-wrap leading-relaxed">{tips}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-mystic-400 mx-auto mb-4" />
                  <p className="text-mystic-200/70">
                    Select any symptoms and click "Get Personalized Tips" to receive AI-powered wellness guidance.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Quick Tips */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="glass-heavy">
          <CardContent className="py-8">
            <h3 className="text-xl font-semibold text-white mb-4 text-center">
              Quick Wellness Prompts
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {[
                "What herbal tea should I drink today?",
                "How can I honor my current cycle phase?",
                "What meditation practice suits this moon phase?"
              ].map((prompt, i) => (
                <Button
                  key={`tip-${i}`}
                  variant="outline"
                  className="border-white/10 text-mystic-200 hover:bg-mystic-700 hover:text-white h-auto py-3 text-left"
                  onClick={() => {
                    setQuestion(prompt);
                    handleGetTips();
                  }}
                  data-testid={`quick-prompt-${i}`}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AITips;
